import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, ChevronLeft, ChevronRight,
  Mail, Phone, CreditCard, Edit2, Eye, X, Save,
  Users
} from 'lucide-react';

// --- SHARED HELPERS ---
const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString('en-IN', {
    year: 'numeric', month: 'short', day: '2-digit'
  });
};

const Avatar = ({ name }) => {
  const initial = name ? name.trim()[0].toUpperCase() : '?';
  const colors = ['#667eea', '#f5576c', '#4facfe', '#43e97b'];
  const color = colors[name.length % colors.length];
  return (
    <div className="avatar-circle" style={{ backgroundColor: color, color: '#fff' }}>
      {initial}
    </div>
  );
};

const Spinner = () => (
  <svg className="spinner" viewBox="0 0 50 50">
    <circle className="path" cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
  </svg>
);

// ==========================================
// EDIT MODAL COMPONENT
// ==========================================
const UserEditModal = ({ user, onClose, onSave, role }) => {
  const [formData, setFormData] = useState({ ...user, referrer_code: user.referrer_code || '' });
  const [loading, setLoading] = useState(false);
  const isViewOnly = role !== 'Admin';

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isViewOnly) return;
    setLoading(true);
    await onSave(formData);
    setLoading(false);
  };

  const modalStyles = `
    .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); backdrop-filter: blur(5px); z-index: 1200; display: flex; align-items: center; justify-content: center; animation: fadeIn 0.3s ease; padding: 20px; box-sizing: border-box; }
    .modal-card { background: white; width: 100%; max-width: 500px; max-height: 90vh; border-radius: 20px; overflow: hidden; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); animation: slideUp 0.4s ease; display: flex; flex-direction: column; }
    .modal-card form { display: flex; flex-direction: column; flex: 1; min-height: 0; }
    .modal-header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 24px; color: white; display: flex; justify-content: space-between; align-items: center; flex-shrink: 0; }
    .modal-title { font-size: 1.25rem; font-weight: 700; margin: 0; display: flex; align-items: center; gap: 10px; }
    .modal-body { padding: 30px; overflow-y: auto; flex: 1; min-height: 0; }
    .form-group { margin-bottom: 20px; }
    .form-label { display: block; font-size: 12px; font-weight: 700; color: #4B5563; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px; }
    .form-input { width: 100%; padding: 12px 16px; border: 1px solid #E5E7EB; border-radius: 10px; font-size: 14px; transition: all 0.2s; box-sizing: border-box; }
    .form-input:focus { outline: none; border-color: #667eea; box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1); }
    .form-input:disabled { background: #F3F4F6; color: #6B7280; cursor: not-allowed; }
    .modal-footer { padding: 20px 30px; background: #F9FAFB; border-top: 1px solid #E5E7EB; display: flex; justify-content: flex-end; gap: 12px; flex-shrink: 0; }
    .btn-cancel { padding: 10px 20px; border-radius: 8px; border: 1px solid #D1D5DB; background: white; font-weight: 600; cursor: pointer; }
    .btn-save { padding: 10px 24px; border-radius: 8px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; border: none; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 8px; }
    .btn-save:disabled { opacity: 0.7; cursor: not-allowed; }
    @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
  `;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <style>{modalStyles}</style>
      <div className="modal-card" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">
            {isViewOnly ? <Eye size={20} /> : <Edit2 size={20} />}
            {isViewOnly ? 'View Profile' : 'Edit User Profile'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}>
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Full Name</label>
              {/* Added fallback to empty string */}
              <input name="full_name" value={formData.full_name || ''} onChange={handleChange} className="form-input" disabled={isViewOnly} required />
            </div>
            <div className="form-group">
              <label className="form-label">Email Address</label>
              {/* Added fallback to empty string */}
              <input name="email" value={formData.email || ''} onChange={handleChange} className="form-input" disabled={isViewOnly} required type="email" />
            </div>
            <div className="form-group">
              <label className="form-label">Mobile Number</label>
              {/* --- FIX HERE: Added fallback for null mobile_no --- */}
              <input name="mobile_no" value={formData.mobile_no || ''} onChange={handleChange} className="form-input" disabled={isViewOnly} required />
            </div>
            <div className="form-group">
              <label className="form-label">UPI ID (For Rewards)</label>
              {/* Fallback already existed here, ensured it's robust */}
              <input name="upi_id" value={formData.upi_id || ''} onChange={handleChange} className="form-input" disabled={isViewOnly} placeholder="Not set" />
            </div>
            <div className="form-group">
              <label className="form-label">Referral Code (Read Only)</label>
              {/* Added fallback to empty string */}
              <input value={formData.referral_code || ''} className="form-input" disabled style={{ backgroundColor: '#EEF2FF', color: '#4F46E5', fontWeight: '700' }} />
            </div>
            <div className="form-group">
              <label className="form-label">Referred By (Referrer's Code)</label>
              <input
                name="referrer_code"
                value={formData.referrer_code}
                onChange={handleChange}
                className="form-input"
                disabled={isViewOnly}
                placeholder="e.g. PRANAV-ETHANS-FWWP (leave blank for none)"
              />
              {!isViewOnly && (
                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '6px' }}>
                  Enter the referring user's referral code to link/change it, or clear this field to remove the referral.
                </div>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" onClick={onClose} className="btn-cancel">Close</button>
            {!isViewOnly && (
              <button type="submit" className="btn-save" disabled={loading}>
                {loading ? 'Saving...' : <><Save size={16} /> Save Changes</>}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

// ==========================================
// REFERRAL NETWORK MODAL
// ==========================================
const ReferralNetworkModal = ({ onClose }) => {
  const [network, setNetwork] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchNetwork = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/referral-network?search=${searchTerm}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setNetwork(data);
      }
    } catch (error) {
      console.error("Fetch network error", error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => fetchNetwork(), 500);
    return () => clearTimeout(timer);
  }, [fetchNetwork]);

  const modalStyles = `
    .net-modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); backdrop-filter: blur(5px); z-index: 1200; display: flex; align-items: center; justify-content: center; animation: fadeIn 0.3s ease; padding: 20px; box-sizing: border-box; }
    .net-modal-card { background: white; width: 100%; max-width: 800px; height: 85vh; border-radius: 20px; overflow: hidden; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25); animation: slideUp 0.4s ease; display: flex; flex-direction: column; }
    .net-modal-header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 24px; color: white; display: flex; flex-direction: column; gap: 16px; flex-shrink: 0; }
    .net-header-top { display: flex; justify-content: space-between; align-items: center; }
    .net-modal-title { font-size: 1.25rem; font-weight: 800; margin: 0; display: flex; align-items: center; gap: 10px; }
    .net-search-container { position: relative; width: 100%; }
    .net-search-input { width: 100%; padding: 12px 16px 12px 48px; border-radius: 12px; border: 2px solid rgba(255,255,255,0.3); background: rgba(255,255,255,0.15); color: white; font-size: 14px; box-sizing: border-box; }
    .net-search-input::placeholder { color: rgba(255,255,255,0.7); }
    .net-search-input:focus { outline: none; background: rgba(255,255,255,0.25); border-color: white; }
    .net-search-icon { position: absolute; left: 16px; top: 50%; transform: translateY(-50%); color: rgba(255,255,255,0.7); }
    .net-modal-body { flex: 1; overflow-y: auto; padding: 0; background: #F9FAFB; }
    .referrer-card { background: white; border-bottom: 1px solid #E5E7EB; padding: 24px; transition: background 0.2s; }
    .referrer-card:hover { background: #F3F4F6; }
    .referrer-header { display: flex; align-items: center; gap: 16px; margin-bottom: 16px; }
    .referrer-info { flex: 1; }
    .referrer-name { font-size: 16px; font-weight: 800; color: #111827; display: flex; align-items: center; gap: 8px; }
    .referrer-badge { background: #EEF2FF; color: #4F46E5; padding: 4px 10px; border-radius: 20px; font-size: 11px; font-weight: 700; text-transform: uppercase; }
    .referred-list { margin-left: 56px; border-left: 2px dashed #D1D5DB; padding-left: 24px; display: flex; flex-direction: column; gap: 12px; }
    .referred-item { background: white; border: 1px solid #E5E7EB; border-radius: 12px; padding: 12px 16px; display: flex; align-items: center; gap: 12px; box-shadow: 0 2px 4px rgba(0,0,0,0.02); }
    .net-empty { padding: 60px 20px; text-align: center; color: #6B7280; font-size: 15px; }
  `;

  return (
    <div className="net-modal-overlay" onClick={onClose}>
      <style>{modalStyles}</style>
      <div className="net-modal-card" onClick={e => e.stopPropagation()}>
        <div className="net-modal-header">
          <div className="net-header-top">
            <h3 className="net-modal-title">
              <Users size={22} color="#FFD700" />
              Referral Network
            </h3>
            <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', cursor: 'pointer', padding: '6px', borderRadius: '50%', display: 'flex' }}>
              <X size={20} />
            </button>
          </div>
          <div className="net-search-container">
            <Search className="net-search-icon" size={18} />
            <input
              type="text"
              className="net-search-input"
              placeholder="Search by referrer or referred user's name, email, etc..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="net-modal-body">
          {loading ? (
            <div style={{ padding: '80px', textAlign: 'center', color: '#6B7280', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
              <Spinner />
              <p>Loading network...</p>
            </div>
          ) : network.length === 0 ? (
            <div className="net-empty">
              <Users size={48} style={{ opacity: 0.2, marginBottom: '16px' }} />
              <p>No referrals found matching your search.</p>
            </div>
          ) : (
            network.map(referrer => (
              <div key={referrer.id} className="referrer-card">
                <div className="referrer-header">
                  <Avatar name={referrer.full_name} />
                  <div className="referrer-info">
                    <div className="referrer-name">
                      {referrer.full_name}
                      <span className="referrer-badge">Referrer</span>
                    </div>
                    <div className="contact-row" style={{ marginTop: '4px' }}>
                      <Mail size={12} /> {referrer.email} &nbsp;•&nbsp; <Phone size={12} /> {referrer.mobile_no || 'N/A'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: '13px', fontWeight: '600', color: '#10B981' }}>
                    {referrer.referredUsers.length} People Referred
                  </div>
                </div>

                <div className="referred-list">
                  {referrer.referredUsers.map(child => (
                    <div key={child.id} className="referred-item">
                      <Avatar name={child.full_name} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '700', color: '#374151', fontSize: '14px' }}>{child.full_name}</div>
                        <div className="contact-row" style={{ marginTop: '2px', fontSize: '12px' }}>
                          <Mail size={10} /> {child.email}
                        </div>
                      </div>
                      <div style={{ fontSize: '12px', color: '#9CA3AF', fontWeight: '500' }}>
                        Joined: {formatDateTime(child.created_at)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// ==========================================
// MAIN PAGE COMPONENT
// ==========================================
const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [editingUser, setEditingUser] = useState(null);
  const [showNetworkModal, setShowNetworkModal] = useState(false);

  const itemsPerPage = 10; // Pagination limit to prevent browser crash
  const role = localStorage.getItem('admin_role');

  const fetchUsers = useCallback(async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      // Calls the backend route with pagination params
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/all-users?page=${currentPage}&limit=${itemsPerPage}&search=${searchTerm}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      if (response.ok) {
        setUsers(data.users);
        setTotalPages(data.totalPages);
        setTotalItems(data.totalItems);
      }
    } catch (error) {
      console.error("Fetch error", error);
    } finally {
      if (!isSilent) setLoading(false);
    }
  }, [currentPage, searchTerm]);

  // Debounce Search: Waits 500ms after typing stops before searching
  useEffect(() => {
    const timer = setTimeout(() => fetchUsers(), 500);
    return () => clearTimeout(timer);
  }, [fetchUsers]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleSaveUser = async (updatedData) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/users/${updatedData.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(updatedData)
      });

      if (response.ok) {
        alert("User updated successfully");
        setEditingUser(null);
        fetchUsers(true); // Silent refresh
      } else {
        const err = await response.json();
        alert(err.message);
      }
    } catch (error) {
      alert("Update failed");
    }
  };

  const pageCss = `
    @keyframes fadeInUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes spin { to { transform: rotate(360deg); } }
    
    .users-page { padding: 0; font-family: 'Inter', sans-serif; animation: fadeIn 0.6s ease-out; }
    
    /* VIOLET GRADIENT HEADER */
    .users-header { 
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); 
      border-radius: 24px; 
      padding: 32px 40px; 
      margin-bottom: 32px; 
      box-shadow: 0 20px 60px rgba(102, 126, 234, 0.3); 
      position: relative; 
      overflow: hidden; 
      animation: fadeInUp 0.6s ease-out;
    }
    .users-header::before { 
      content: ''; position: absolute; top: -50%; right: -50%; width: 200%; height: 200%; 
      background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%); 
      animation: spin 20s linear infinite; pointer-events: none;
    }
    
    .header-content { 
      position: relative; z-index: 1; 
      display: flex; justify-content: space-between; align-items: flex-end; 
      flex-wrap: wrap; gap: 20px; 
    }
    
    .page-title { margin: 0; font-size: clamp(1.8rem, 4vw, 2.5rem); font-weight: 900; color: #FFFFFF; line-height: 1.2; }
    .page-subtitle { color: rgba(255,255,255,0.9); margin: 8px 0 0 0; font-size: 1rem; }
    
    /* YELLOW GRADIENT TEXT for "Users" */
    .gradient-text { 
      background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%); 
      -webkit-background-clip: text; 
      -webkit-text-fill-color: transparent; 
      display: inline-block; 
    }

    /* SEARCH BOX with Icon Inside */
    .search-bar-container { position: relative; min-width: 320px; }
    .search-input { 
      width: 100%; padding: 12px 16px 12px 48px; /* Extra left padding for icon */
      border-radius: 12px; 
      border: 2px solid rgba(255,255,255,0.3); background: rgba(255,255,255,0.15); 
      color: white; font-size: 14px; backdrop-filter: blur(5px); 
      transition: all 0.3s; box-sizing: border-box; 
    }
    .search-input::placeholder { color: rgba(255,255,255,0.7); }
    .search-input:focus { outline: none; background: rgba(255,255,255,0.25); border-color: white; box-shadow: 0 0 0 4px rgba(255,255,255,0.1); }
    .search-icon { 
      position: absolute; left: 16px; top: 50%; transform: translateY(-50%); 
      color: rgba(255,255,255,0.7); pointer-events: none; z-index: 2;
    }

    .table-card { background: #FFFFFF; border-radius: 20px; border: 1px solid #E5E7EB; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04); overflow: hidden; animation: fadeInUp 0.6s ease-out 0.2s forwards; opacity: 0; }
    .user-table { width: 100%; border-collapse: collapse; }
    .user-table th { background: #F9FAFB; padding: 20px 24px; text-align: left; font-size: 12px; font-weight: 800; color: #6B7280; text-transform: uppercase; border-bottom: 1px solid #E5E7EB; white-space: nowrap; }
    .user-table td { padding: 20px 24px; border-bottom: 1px solid #F3F4F6; vertical-align: middle; font-size: 14px; color: #1F2937; }
    .user-table tr:hover { background: #F9FAFB; }
    
    .user-cell { display: flex; align-items: center; gap: 12px; }
    .avatar-circle { width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 16px; box-shadow: 0 4px 10px rgba(0,0,0,0.1); }
    .user-name { font-weight: 700; color: #111827; display: block; }
    
    .contact-row { display: flex; align-items: center; gap: 8px; color: #4B5563; font-size: 13px; margin-bottom: 4px; }
    .upi-badge { background: #ECFDF5; color: #065F46; padding: 4px 10px; border-radius: 6px; font-size: 12px; font-weight: 700; display: inline-block; border: 1px solid #A7F3D0; }
    .no-upi { color: #9CA3AF; font-size: 12px; font-style: italic; }
    .code-box { background: #F3F4F6; padding: 6px 12px; border-radius: 8px; font-family: monospace; font-weight: 700; color: #374151; border: 1px solid #E5E7EB; display: inline-block; }
    
    .action-btn { padding: 8px 16px; border-radius: 8px; border: 1px solid; font-size: 13px; font-weight: 700; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: all 0.2s; }
    .btn-edit { border-color: #667eea; color: #667eea; background: white; }
    .btn-edit:hover { background: #EEF2FF; transform: translateY(-2px); }
    .btn-view { border-color: #9CA3AF; color: #4B5563; background: white; }
    .btn-view:hover { background: #F3F4F6; transform: translateY(-2px); }

    /* PAGINATION STYLES */
    .pagination { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px; background: #F9FAFB; border-top: 1px solid #E5E7EB; }
    .page-btn { padding: 8px 16px; border: 1px solid #E5E7EB; background: white; border-radius: 8px; cursor: pointer; display: flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 600; color: #374151; transition: all 0.2s; }
    .page-btn:hover:not(:disabled) { background: #F3F4F6; border-color: #D1D5DB; }
    .page-btn:disabled { opacity: 0.5; cursor: not-allowed; }
    
    .spinner { animation: spin 1s linear infinite; width: 2.5rem; height: 2.5rem; color: #667eea; }
    .spinner .path { stroke: currentColor; stroke-linecap: round; }
    
    @media (max-width: 768px) {
      .users-header { padding: 24px; }
      .page-title { font-size: 1.5rem; }
      .search-bar-container { width: 100%; min-width: 0; margin-top: 16px; }
      .user-table th, .user-table td { padding: 12px 16px; }
      .pagination { flex-direction: column; gap: 12px; }
    }
  `;

  return (
    <div className="users-page">
      <style>{pageCss}</style>

      {/* Header with Violet Gradient */}
      <div className="users-header">
        <div className="header-content">
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
              <span style={{ background: 'rgba(255,255,255,0.2)', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', color: 'white' }}>
                Database
              </span>
            </div>
            <h1 className="page-title">
              Registered <span className="gradient-text">Users</span> {/* Yellow Gradient Text */}
            </h1>
            <p className="page-subtitle">Manage {totalItems} user profiles</p>
          </div>

          {/* Search with Icon Inside & Network Button */}
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="search-bar-container">
              <Search className="search-icon" size={20} />
              <input
                type="text"
                className="search-input"
                placeholder="Search by name, email, mobile..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button
              onClick={() => setShowNetworkModal(true)}
              style={{
                padding: '12px 20px', borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.4)',
                background: 'rgba(255, 255, 255, 0.1)', color: 'white', fontWeight: '600',
                display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer',
                backdropFilter: 'blur(10px)', transition: 'all 0.2s', boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
              }}
              onMouseOver={e => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
              onMouseOut={e => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
            >
              <Users size={18} />
              View Referral Network
            </button>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="table-card">
        {loading && !users.length ? (
          <div style={{ padding: '80px', textAlign: 'center', color: '#6B7280', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
            <Spinner />
            <p>Loading users...</p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className="user-table">
                <thead>
                  <tr>
                    <th>User Profile</th>
                    <th>Contact Details</th>
                    <th>Rewards Info</th>
                    <th>Referral Code</th>
                    <th>Referred By</th>
                    <th>Joined Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id}>
                      <td>
                        <div className="user-cell">
                          <Avatar name={user.full_name} />
                          <div>
                            <span className="user-name">{user.full_name}</span>
                            <span style={{ fontSize: '12px', color: '#9CA3AF' }}>ID: {user.id}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="contact-row"><Mail size={12} /> {user.email}</div>
                        <div className="contact-row"><Phone size={12} /> {user.mobile_no}</div>
                      </td>
                      <td>
                        {user.upi_id ? (
                          <span className="upi-badge"><CreditCard size={10} style={{ marginRight: '4px', display: 'inline' }} />{user.upi_id}</span>
                        ) : (
                          <span className="no-upi">Not Linked</span>
                        )}
                      </td>
                      <td><span className="code-box">{user.referral_code}</span></td>
                      <td>
                        {user.referrer_name ? (
                          <span style={{ fontWeight: 600, color: '#374151' }}>{user.referrer_name}</span>
                        ) : (
                          <span className="no-upi">None</span>
                        )}
                      </td>
                      <td>{formatDateTime(user.created_at)}</td>
                      <td>
                        {role === 'Admin' ? (
                          <button className="action-btn btn-edit" onClick={() => setEditingUser(user)}>
                            <Edit2 size={14} /> Edit
                          </button>
                        ) : (
                          <button className="action-btn btn-view" onClick={() => setEditingUser(user)}>
                            <Eye size={14} /> View
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr><td colSpan="7" style={{ textAlign: 'center', padding: '60px', color: '#6B7280' }}>No users found matching your search.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <div className="pagination">
              <span style={{ fontSize: '13px', color: '#6B7280', fontWeight: '500' }}>
                Showing {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} users
              </span>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="page-btn" disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}>
                  <ChevronLeft size={14} /> Previous
                </button>
                <button className="page-btn" disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}>
                  Next <ChevronRight size={14} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Edit/View Modal */}
      {editingUser && (
        <UserEditModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSave={handleSaveUser}
          role={role}
        />
      )}

      {/* Network Modal */}
      {showNetworkModal && (
        <ReferralNetworkModal onClose={() => setShowNetworkModal(false)} />
      )}
    </div>
  );
};

export default UsersPage;